// @flow
import styled from '@emotion/styled';
import { color, COLORS, SPACING, typography } from '@tablekit/theme';
import Link from 'components/GatsbyLink';
import { MOBILE_BREAKPOINT } from 'utils/theme';
import { GridOuter } from '../styled';

type PropsType = {
  baseSlug: string,
  currentPage: number,
  totalPages: number
};

const Wrapper = styled(GridOuter)`
  display: flex;
  justify-content: flex-end;

  @media ${MOBILE_BREAKPOINT} {
    margin-top: ${SPACING.L8};
    padding: 0 ${SPACING.L2};
  }
`;

const PaginationLinkBase = styled.div`
  ${typography('L4')};
  margin: ${parseInt(SPACING.L3, 10) / 2}px;
  width: 32px;
  height: 32px;
  text-align: center;
  line-height: 32px;

  @media ${MOBILE_BREAKPOINT} {
    margin: ${parseInt(SPACING.L6, 10) / 2}px;
  }

  &,
  &:hover,
  &:active,
  &:visited {
    color: ${color('text')};
    text-decoration: none;
  }
`;

const PaginationLink = styled(PaginationLinkBase.withComponent(Link))`
  border: 1px solid ${COLORS.GRAY.L4};
  &:hover {
    background: ${COLORS.GRAY.L2};
  }
`;

const CurrentPage = styled(PaginationLinkBase)`
  background: ${COLORS.GRAY.L2};
`;

const Pagination = ({ baseSlug, currentPage, totalPages }: PropsType) => {
  if (totalPages === 1) return null;
  if (
    process.env.NODE_ENV !== 'production' &&
    baseSlug[baseSlug.length - 1] !== '/'
  ) {
    console.warn(
      `Pagination Component is passed an incorrect baseSlug, "${baseSlug}" should be "${baseSlug}/"`
    );
  }
  const paginationStart = Math.max(1, currentPage - 1);
  const paginationEnd = Math.min(totalPages, paginationStart + 2);
  const pagination = [];
  if (paginationStart > 1) {
    pagination.push(
      <PaginationLink key={1} to={baseSlug}>
        1
      </PaginationLink>
    );
    if (paginationStart > 2) {
      pagination.push(
        <PaginationLinkBase key="start-ellipsis">...</PaginationLinkBase>
      );
    }
  }
  for (let i = paginationStart; i <= paginationEnd; i += 1) {
    if (i === currentPage) {
      pagination.push(<CurrentPage key={i}>{i}</CurrentPage>);
    } else if (i === 1) {
      pagination.push(
        <PaginationLink key={i} to={baseSlug}>
          {i}
        </PaginationLink>
      );
    } else {
      pagination.push(
        <PaginationLink key={i} to={`${baseSlug}${i}`}>
          {i}
        </PaginationLink>
      );
    }
  }
  if (paginationEnd < totalPages) {
    if (paginationEnd < totalPages - 1) {
      pagination.push(
        <PaginationLinkBase key="end-ellipsis">...</PaginationLinkBase>
      );
    }
    pagination.push(
      <PaginationLink key={totalPages} to={`${baseSlug}${totalPages}`}>
        {totalPages}
      </PaginationLink>
    );
  }
  return <Wrapper data-testid="Blog Pagination">{pagination}</Wrapper>;
};

export default Pagination;
